/**
 * One DOM, two behaviours: a tablist above 990px, a single-open accordion below it.
 *
 * The panels are the same elements in both modes — only the ARIA contract and the
 * open/close mechanics differ, so the open index survives a breakpoint change.
 * Panels stay in the DOM (unhidden) while animating; `hidden` is applied only after
 * a mobile collapse finishes, which keeps the grid-rows transition able to run.
 */
if (!customElements.get('product-info-tabs')) {
  customElements.define(
    'product-info-tabs',
    class ProductInfoTabs extends HTMLElement {
      static DESKTOP_QUERY = '(min-width: 990px)';

      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('.product-info-tabs__tab'));
        this.headers = Array.from(this.querySelectorAll('.product-info-tabs__accordion-header'));
        this.panels = Array.from(this.querySelectorAll('.product-info-tabs__panel'));
        if (!this.panels.length) return;

        this.openIndex = 0;
        this.mediaQuery = window.matchMedia(ProductInfoTabs.DESKTOP_QUERY);

        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        this.mediaQuery.addEventListener('change', this.onBreakpointChange);

        this.render();
      }

      disconnectedCallback() {
        this.mediaQuery?.removeEventListener('change', this.onBreakpointChange);
      }

      get isDesktop() {
        return this.mediaQuery.matches;
      }

      /**
       * Re-applies the ARIA contract and the open/close mechanics for the new mode.
       * The open index carries over, so the tab you were reading stays the panel you see.
       */
      onBreakpointChange() {
        // A panel mid-transition on the old mode must not keep a stale transitional state.
        this.panels.forEach((panel) => panel.classList.remove('is-open'));
        this.render();
      }

      onClick(event) {
        const control = event.target.closest('.product-info-tabs__tab, .product-info-tabs__accordion-header');
        if (!control || !this.contains(control)) return;

        const index = this.indexOfControl(control);
        if (index === -1) return;

        // On mobile the open panel toggles shut; on desktop a tab is always selected.
        const shouldCollapse = !this.isDesktop && index === this.openIndex;
        this.openIndex = shouldCollapse ? -1 : index;
        this.render();
      }

      onKeydown(event) {
        const tab = event.target.closest('.product-info-tabs__tab');
        if (!tab || !this.isDesktop) return;

        const lastIndex = this.tabs.length - 1;
        const current = this.tabs.indexOf(tab);
        let next = null;

        switch (event.key) {
          case 'ArrowRight':
            next = current === lastIndex ? 0 : current + 1;
            break;
          case 'ArrowLeft':
            next = current === 0 ? lastIndex : current - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = lastIndex;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.openIndex = next;
        this.render();
        this.tabs[next].focus();
      }

      indexOfControl(control) {
        const tabIndex = this.tabs.indexOf(control);
        return tabIndex === -1 ? this.headers.indexOf(control) : tabIndex;
      }

      render() {
        const { isDesktop } = this;

        // Desktop always keeps one panel open; a collapsed accordion must not survive a resize.
        if (isDesktop && this.openIndex === -1) this.openIndex = 0;

        this.panels.forEach((panel, index) => {
          const isOpen = index === this.openIndex;

          this.tabs[index]?.setAttribute('aria-selected', String(isOpen));
          this.tabs[index]?.setAttribute('tabindex', isOpen ? '0' : '-1');
          this.headers[index]?.setAttribute('aria-expanded', String(isOpen));

          // `role`/`aria-labelledby` point at whichever control is visible in this mode,
          // so screen readers never announce a hidden button as the panel's label.
          panel.setAttribute('role', isDesktop ? 'tabpanel' : 'region');
          panel.setAttribute(
            'aria-labelledby',
            (isDesktop ? this.tabs[index] : this.headers[index])?.id ?? ''
          );

          if (isDesktop) {
            panel.classList.remove('is-open');
            panel.toggleAttribute('hidden', !isOpen);
            return;
          }

          this.toggleAccordionPanel(panel, isOpen);
        });
      }

      /**
       * `hidden` and a height transition are mutually exclusive, so the attribute is
       * removed before opening and only re-applied once the closing transition ends.
       */
      toggleAccordionPanel(panel, isOpen) {
        if (isOpen) {
          panel.removeAttribute('hidden');
          // Force a style resolution so the 0fr -> 1fr change is an animatable transition
          // rather than the initial value of a freshly unhidden element.
          requestAnimationFrame(() => panel.classList.add('is-open'));
          return;
        }

        if (!panel.classList.contains('is-open')) {
          panel.setAttribute('hidden', '');
          return;
        }

        panel.classList.remove('is-open');
        panel.addEventListener(
          'transitionend',
          (event) => {
            if (event.propertyName !== 'grid-template-rows') return;
            if (!panel.classList.contains('is-open')) panel.setAttribute('hidden', '');
          },
          { once: true }
        );
      }
    }
  );
}
